import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type SiteThemeResponse } from "@/lib/api";
import {
  BUTTON_SHAPE_RADIUS,
  DEFAULT_THEME,
  getFontFamily,
  getFontPair,
  getPalette,
} from "@/cms/themePresets";

/**
 * Loads the site theme and applies its tokens to <html>. Should be called
 * once near the root of the app — repeated calls just refresh the same
 * CSS-vars on the same element.
 *
 * Resolution: palette tokens (HSL strings) → overrides (hex, converted to
 * HSL). Font families + button radius are also injected as CSS-vars so the
 * Button component and Tailwind utilities can pick them up.
 */
export function useApplySiteTheme(): SiteThemeResponse {
  const { data } = useQuery({
    queryKey: ["site-theme"],
    queryFn: api.getSiteTheme,
    staleTime: 5_000,
  });
  const theme = data ?? (DEFAULT_THEME as SiteThemeResponse);

  useEffect(() => {
    const root = document.documentElement;
    const palette = getPalette(theme.paletteId);
    for (const [token, hsl] of Object.entries(palette.tokens)) {
      root.style.setProperty(token, hsl);
    }
    for (const [token, hex] of Object.entries(theme.overrides ?? {})) {
      if (!hex) continue;
      const hsl = hexToHsl(hex);
      if (hsl) root.style.setProperty(token, hsl);
    }
    // Resolution: pair sets defaults; per-family overrides win when present.
    const pair = getFontPair(theme.fontPairId);
    const heading = getFontFamily(theme.headingFont);
    const body = getFontFamily(theme.bodyFont);
    root.style.setProperty("--font-heading", heading?.family ?? pair.headingFamily);
    root.style.setProperty("--font-body", body?.family ?? pair.bodyFamily);
    root.style.setProperty("--btn-radius", BUTTON_SHAPE_RADIUS[theme.buttonShape]);
    root.setAttribute("data-button-fill", theme.buttonFill);
  }, [
    theme.paletteId,
    theme.fontPairId,
    theme.headingFont,
    theme.bodyFont,
    theme.buttonShape,
    theme.buttonFill,
    theme.overrides,
  ]);

  return theme;
}

/** Hex (#rrggbb) → CSS HSL triple ("h s% l%"). Returns null on invalid input. */
export function hexToHsl(hex: string): string | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
